import { BAT_PREVIEW_MAX_WIDTH_PX } from '@/domain/product/productDimensions';
import { canvasToBlob, runExportPipeline } from '@/infrastructure/engine2d/export/runExportPipeline';
import { EExportRenderingMode } from '@/application/ports/EExportRenderingMode';
import type { I2DExporter } from '@/application/ports/I2DExporter';
import type { FabricScene } from '@/infrastructure/engine2d/FabricScene';
import type { TWatermarkAsset } from '@/infrastructure/engine2d/export/applyWatermarkToCanvas';

type TFabricExporter2DParams = {
    scene: FabricScene;
};

const THUMBNAIL_JPEG_QUALITY = 0.9;
const BAT_PREVIEW_WIDTH_PX = Math.round(BAT_PREVIEW_MAX_WIDTH_PX * 0.75);
// The customer's proof carries the configurator's own logo, "Mon gobelet personnalisé" (Katia,
// 2026-10-09), tilted and see-through in the file itself, in place of the socle's "Your company".
const BAT_WATERMARK: TWatermarkAsset = {
    scale: { x: 2.5, y: 2.5 },
    size: { height: 122, width: 200 },
    url: '/images/watermark-gobeletcup.svg',
};

// Shares the FabricScene with the sibling FabricEngine2D — both are
// wired by createFabricAdapters so exports and mutations stay in
// lock-step. Every method composes the same pipeline
// (getZoneCanvas → watermark? → reduce? → toBlob?), so the class is
// just intent-per-caller thin plumbing.
class FabricExporter2D implements I2DExporter {
    private readonly _scene: FabricScene;

    constructor(params: TFabricExporter2DParams) {
        this._scene = params.scene;
    }

    exportThumbnail({
        mode = EExportRenderingMode.BatPreview,
        width,
    }: {
        mode?: EExportRenderingMode;
        width: number;
    }): Promise<Blob | undefined> {
        return runExportPipeline({
            backgroundColor: this._jpegSafeBackground(),
            fabricWorld: this._scene,
            mode,
            reduceToWidth: width,
        })
            .then((canvas) => canvasToBlob(canvas, 'image/jpeg', THUMBNAIL_JPEG_QUALITY))
            .then((blob) => blob ?? undefined);
    }

    exportPreviewZoneCanvas(): Promise<{ canvas: HTMLCanvasElement; hasOpaqueBackground: boolean }> {
        return runExportPipeline({
            backgroundColor: this._scene.canvasBackgroundColor,
            fabricWorld: this._scene,
            mode: EExportRenderingMode.Texture,
        }).then((canvas) => ({
            canvas,
            hasOpaqueBackground: this._scene.canvasBackgroundColor !== 'transparent' && this._scene.canvasBackgroundColor !== '',
        }));
    }

    exportPrintBlob(): Promise<Blob | null> {
        return runExportPipeline({
            backgroundColor: this._scene.canvasBackgroundColor,
            fabricWorld: this._scene,
            mode: EExportRenderingMode.Print,
        }).then((canvas) => canvasToBlob(canvas, 'image/png', 1));
    }

    exportWatermarkedPreview(): Promise<Blob | undefined> {
        return runExportPipeline({
            backgroundColor: this._scene.canvasBackgroundColor,
            fabricWorld: this._scene,
            mode: EExportRenderingMode.BatPreview,
            reduceToWidth: BAT_PREVIEW_WIDTH_PX,
            watermark: BAT_WATERMARK,
        })
            .then((canvas) => canvasToBlob(canvas, 'image/png', 1))
            .then((blob) => blob ?? undefined)
            .catch((error) => {
                console.error('exportWatermarkedPreview: BAT preview failed', error);
                return undefined;
            });
    }

    // JPEG has no alpha — transparent pixels get encoded as black.
    // Fall back to white when the scene has no opaque background so
    // the interior of a cut shape reads as paper, not as a black hole.
    private _jpegSafeBackground(): string {
        const sceneBg = this._scene.canvasBackgroundColor;
        return sceneBg && sceneBg !== 'transparent' ? sceneBg : '#ffffff';
    }
}

export { FabricExporter2D };
